// src/pages/CustomerAdminPage.js

import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import SaveIcon from '@mui/icons-material/Save'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'
import DomainIcon from '@mui/icons-material/Domain'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import { collection, doc, getDocs, orderBy, query, updateDoc } from 'firebase/firestore'
import { db } from '../firebase'
import { useAuth } from '../contexts/AuthContext'
import { getNextResetDate, normalizePlanData } from '../utils/planUtils'

const SUPER_ADMIN_EMAIL = 'serkan@evoosurvey.com'

const CustomerAdminPage = () => {
  const { currentUser } = useAuth()
  const [loading, setLoading] = useState(true)
  const [organizations, setOrganizations] = useState([])
  const [users, setUsers] = useState([])
  const [error, setError] = useState(null)
  const [savingOrgId, setSavingOrgId] = useState(null)
  const [orgDrafts, setOrgDrafts] = useState({})

  const isAuthorized = currentUser?.email === SUPER_ADMIN_EMAIL

  const loadData = async () => {
    setLoading(true)
    setError(null)
    try {
      const orgSnap = await getDocs(query(collection(db, 'organizations'), orderBy('name', 'asc')))
      const orgs = orgSnap.docs.map((d) => normalizePlanData({ id: d.id, ...d.data() }))
      setOrganizations(orgs)
      setOrgDrafts({})

      const usersSnap = await getDocs(query(collection(db, 'users')))
      const allUsers = usersSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
      setUsers(allUsers)
    } catch (err) {
      console.error('Customer admin load error:', err)
      setError('Veriler alınamadı. Lütfen tekrar deneyin.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!isAuthorized) return
    loadData()
  }, [isAuthorized])

  const usersByOrg = useMemo(() => {
    const map = {}
    users.forEach((u) => {
      const orgId = u.organizationId || 'none'
      if (!map[orgId]) map[orgId] = []
      map[orgId].push(u)
    })
    return map
  }, [users])

  const handleSaveOrg = async (orgId, updates) => {
    setSavingOrgId(orgId)
    try {
      await updateDoc(doc(db, 'organizations', orgId), updates)
      await loadData()
    } catch (err) {
      console.error('Org update error:', err)
      setError('Plan bilgisi kaydedilemedi.')
    } finally {
      setSavingOrgId(null)
    }
  }

  const handleResetQuota = async (org) => {
    await handleSaveOrg(org.id, {
      responsesUsed: 0,
      responseResetAt: getNextResetDate()
    })
  }

  const setDraft = (orgId, key, value) => {
    setOrgDrafts((prev) => ({
      ...prev,
      [orgId]: {
        ...(prev[orgId] || {}),
        [key]: value
      }
    }))
  }

  const handleUserRole = async (userId, nextRole) => {
    try {
      await updateDoc(doc(db, 'users', userId), { role: nextRole })
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role: nextRole } : u)))
    } catch (err) {
      console.error('User role update error:', err)
      setError('Kullanıcı rolü güncellenemedi.')
    }
  }

  if (!isAuthorized) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Bu sayfayı yalnızca yetkili hesap görebilir.</Alert>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        maxWidth: 960,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Müşteri Yönetim Ekranı
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Organizasyonları, kullanıcıları ve yanıt kotalarını buradan yönet.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadData}
          disabled={loading}
        >
          Yenile
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2.5}>
          {organizations.map((org) => {
            const orgUsers = usersByOrg[org.id] || []
            const remaining = Math.max((org.responseLimit || 0) - (org.responsesUsed || 0), 0)
            return (
              <Paper
                key={org.id}
                variant="outlined"
                sx={{
                  p: { xs: 2, md: 2.5 },
                  borderRadius: 3,
                  borderColor: 'rgba(148,163,184,0.28)',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 10px 30px rgba(15,23,42,0.06)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2
                }}
              >
                {/* Card header - org avatar + name + quick action */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1.5
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background:
                          'linear-gradient(135deg, rgba(59,130,246,0.16), rgba(37,99,235,0.22))',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <DomainIcon fontSize="small" />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {org.name || 'İsimsiz Organizasyon'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Org ID: {org.id}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RestartAltIcon />}
                    disabled={savingOrgId === org.id}
                    onClick={() => handleResetQuota(org)}
                    sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 600 }}
                  >
                    Kotayı Sıfırla
                  </Button>
                </Box>

                {/* Plan / quota chips - like meta row under a post */}
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  sx={{ mt: 0.5 }}
                >
                  <Chip
                    label={`Plan: ${org.planName || 'Free'}`}
                    color="primary"
                    size="small"
                    icon={<ShieldOutlinedIcon />}
                    sx={{ borderRadius: 999 }}
                  />
                  <Chip
                    label={`${org.responsesUsed || 0}/${org.responseLimit || 0} kullanıldı`}
                    size="small"
                    sx={{ borderRadius: 999 }}
                  />
                  <Chip
                    label={`Kalan: ${remaining}`}
                    size="small"
                    color="success"
                    sx={{ borderRadius: 999 }}
                  />
                </Stack>

                <Divider />

                {/* Content section - left: plan edit, right: users list */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2
                  }}
                >
                  {/* Left column: plan fields */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack spacing={1.5}>
                      <TextField
                        size="small"
                        label="Plan adı"
                        value={orgDrafts[org.id]?.planName ?? org.planName ?? ''}
                        onChange={(e) => setDraft(org.id, 'planName', e.target.value)}
                      />
                      <TextField
                        size="small"
                        type="number"
                        label="Aylık limit"
                        value={orgDrafts[org.id]?.responseLimit ?? org.responseLimit ?? 0}
                        onChange={(e) => setDraft(org.id, 'responseLimit', Number(e.target.value))}
                      />
                      <TextField
                        size="small"
                        type="number"
                        label="Kullanılan yanıt"
                        value={orgDrafts[org.id]?.responsesUsed ?? org.responsesUsed ?? 0}
                        onChange={(e) => setDraft(org.id, 'responsesUsed', Number(e.target.value))}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant="contained"
                          startIcon={<SaveIcon />}
                          disabled={savingOrgId === org.id}
                          onClick={() =>
                            handleSaveOrg(org.id, {
                              planName:
                                orgDrafts[org.id]?.planName ??
                                org.planName ??
                                'Free',
                              responseLimit:
                                orgDrafts[org.id]?.responseLimit ??
                                org.responseLimit ??
                                0,
                              responsesUsed:
                                orgDrafts[org.id]?.responsesUsed ??
                                org.responsesUsed ??
                                0
                            })
                          }
                          sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 600 }}
                        >
                          Kaydet
                        </Button>
                      </Box>
                    </Stack>
                  </Box>

                  {/* Right column: users list as comments thread */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                      <PeopleOutlineIcon color="action" />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Kullanıcılar ({orgUsers.length})
                      </Typography>
                    </Stack>
                    <Stack
                      spacing={1}
                      sx={{
                        maxHeight: 220,
                        overflowY: 'auto',
                        borderRadius: 2,
                        p: 1,
                        backgroundColor: 'rgba(15,23,42,0.02)'
                      }}
                    >
                      {orgUsers.map((u) => (
                        <Stack
                          key={u.id}
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{
                            p: 0.75,
                            borderRadius: 1.5,
                            backgroundColor: '#f8fafc'
                          }}
                        >
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {u.firstName || u.lastName ? `${u.firstName} ${u.lastName}` : u.email}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {u.email}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Chip
                              label={u.role || 'user'}
                              size="small"
                              color={u.role === 'admin' ? 'primary' : 'default'}
                              sx={{ borderRadius: 1.5 }}
                            />
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleUserRole(u.id, u.role === 'admin' ? 'user' : 'admin')
                              }
                            >
                              <AdminPanelSettingsIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Stack>
                      ))}
                      {!orgUsers.length && (
                        <Typography variant="caption" color="text.secondary">
                          Bu organizasyona bağlı kullanıcı yok.
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                </Box>
              </Paper>
            )
          })}
        </Stack>
      )}
    </Box>
  )
}

export default CustomerAdminPage
